import { storeToRefs } from 'pinia';
import { useUserStore } from '@/store/userStore';
import { can, AppPermissions } from '@/utils/permissions';
import { useSchoolLabels } from '@/composables/useSchoolLabels';
import type { CourseLecturer, Staff, StaffSubjectGroup } from '@/types/composition-functions';

/**
 * Composable that derives staff assignment data and permission flags
 * from the current user. Drives dashboard visibility and scoping.
 *
 * - No new queries — all data comes from `me.staffProfile`
 * - No role flags — visibility is permission-driven
 * - Works for both secondary (subjects) and tertiary (courses)
 */
export function useStaffDashboard() {
  const { currentUser } = storeToRefs(useUserStore());
  const { labels } = useSchoolLabels();
  const user = currentUser;

  // --- Staff profile (null for non-staff / students) ---
  // Annotated rather than inferred: `currentUser` comes back from storeToRefs as
  // `any`, and without a type here that `any` propagates into every consumer —
  // each callback parameter over `mySubjects`/`myCourses` silently became an
  // implicit any in useScoreEntry, useManageCbtLogic, CopyCBTScores and
  // StaffDepartmentCard. Naming the shape once fixes all of them at the source.
  const staffProfile = computed<Staff | null>(() => user.value?.staffProfile ?? null);

  // --- Permission checks (drive widget visibility) ---
  const canUploadScores = computed(() =>
    can(user.value, AppPermissions.Results.Actions.UploadScores, AppPermissions.Results.Resource),
  );
  const canViewResults = computed(() =>
    can(user.value, AppPermissions.Results.Actions.View, AppPermissions.Results.Resource),
  );
  const canViewSubjects = computed(() =>
    can(user.value, AppPermissions.Subjects.Actions.View, AppPermissions.Subjects.Resource),
  );
  const canManageCBT = computed(() =>
    can(user.value, AppPermissions.CBT.Actions.View, AppPermissions.CBT.Resource),
  );
  const canViewStudents = computed(() =>
    can(user.value, AppPermissions.Students.Actions.View, AppPermissions.Students.Resource),
  );
  const canCreateStudents = computed(() =>
    can(user.value, AppPermissions.Students.Actions.Create, AppPermissions.Students.Resource),
  );
  const canManageAttendance = computed(() =>
    can(user.value, AppPermissions.Attendance.Actions.View, AppPermissions.Attendance.Resource),
  );
  const canCreateAttendance = computed(() =>
    can(user.value, AppPermissions.Attendance.Actions.Create, AppPermissions.Attendance.Resource),
  );
  const canCreateBills = computed(() =>
    can(user.value, AppPermissions.Billing.Actions.CreateBills, AppPermissions.Billing.Resource),
  );

  // --- Assignment data (secondary: subjects → classroomSections) ---
  const mySubjects = computed<StaffSubjectGroup[]>(() => staffProfile.value?.subjects ?? []);
  const mySubjectIds = computed(() =>
    mySubjects.value.map((s) => s.subject.id),
  );
  const myClassroomSectionIds = computed(() => {
    const ids = new Set<string>();
    mySubjects.value.forEach((s) =>
      s.classroomSections.forEach((cs) => ids.add(cs.id)),
    );
    return [...ids];
  });
  const myClassroomIds = computed(() => {
    const ids = new Set<string>();
    mySubjects.value.forEach((s) =>
      s.classroomSections.forEach((cs) => {
        if (cs.classroom) ids.add(cs.classroom.id);
      }),
    );
    return [...ids];
  });

  // --- Assignment data (tertiary: lecturer_courses_in_semester → courses) ---
  const allMyCourses = computed<CourseLecturer[]>(() =>
    staffProfile.value?.lecturer_courses_in_semester ?? [],
  );
  const myCourses = computed<CourseLecturer[]>(() =>
    allMyCourses.value.filter((lc) => lc.is_approved),
  );
  const myCourseIds = computed(() => myCourses.value.map((lc) => lc.course?.id));

  // --- Unified: does this staff have any teaching assignments? ---
  const hasAssignments = computed(
    () => mySubjects.value.length > 0 || myCourses.value.length > 0,
  );

  return {
    staffProfile,
    labels,
    // Permission flags (drive visibility)
    canUploadScores,
    canViewResults,
    canViewSubjects,
    canManageCBT,
    canViewStudents,
    canCreateStudents,
    canManageAttendance,
    canCreateAttendance,
    canCreateBills,
    // Assignment data (drive scoping)
    hasAssignments,
    mySubjects,
    mySubjectIds,
    myClassroomSectionIds,
    myClassroomIds,
    allMyCourses,
    myCourses,
    myCourseIds,
  };
}
